import React, { useState, useEffect } from 'react';
import { GoogleMap, useLoadScript, DirectionsRenderer, Marker, InfoWindow } from '@react-google-maps/api';
import { Button } from './ui/button';
import { Loader, MapPin, Navigation, Star, Car, Expand, Shrink, X } from 'lucide-react';
import axios from 'axios';

const libraries = ['places', 'geometry'];

const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

const WalkRouteMap = ({ walk, compact = false, onPOISelect }) => {
  const [directions, setDirections] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [error, setError] = useState(null);
  const [culturalPOIs, setCulturalPOIs] = useState([]);
  const [poisLoading, setPoisLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Load Google Maps API
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  // Ordered waypoints from the walk document.
  const orderedWaypoints = [...(walk.waypoints || [])]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Helper: returns a usable {lat, lng} or null.
  const toLatLng = (coords) => {
    if (!coords) return null;
    const lat = Number(coords.lat);
    const lng = Number(coords.lng);
    if (isNaN(lat) || isNaN(lng)) return null;
    if (lat === 0 && lng === 0) return null;
    return { lat, lng };
  };

  // Resolved positions (null when coords missing — markers skipped, but
  // the Directions API can still use the address string).
  const [resolvedPositions, setResolvedPositions] = useState([]);

  // Calculate the center point for the map
  const firstWpCoords = orderedWaypoints.length > 0 ? toLatLng(orderedWaypoints[0].coords) : null;
  const mapCenter = firstWpCoords
    || toLatLng(walk.startingCoords)
    || toLatLng(walk.endingCoords)
    || { lat: 34.0522, lng: -118.2437 };

  // Different styles for compact vs full mode
  const mapContainerStyle = compact 
    ? { width: '100%', height: '100%' }
    : { width: '100%', height: '400px' };

  // Fetch Cultural POIs for the walk
  const fetchCulturalPOIs = async () => {
    if (!walk.id && (!walk.routeCoordinates || walk.routeCoordinates.length === 0)) return;
    
    setPoisLoading(true);
    try {
      let response;
      if (walk.id) {
        try {
          // Try to fetch POIs specific to this walk
          response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/walks/${walk.id}/cultural-pois`
          );
          setCulturalPOIs(response.data.cultural_pois || []);
        } catch (walkError) {
          // Fall back to coordinate-based search
          if (walk.routeCoordinates && walk.routeCoordinates.length > 0) {
            const centerCoord = walk.routeCoordinates[Math.floor(walk.routeCoordinates.length / 2)];
            response = await axios.get(
              `${process.env.REACT_APP_BACKEND_URL}/api/cultural-pois/search`,
              {
                params: {
                  lat: centerCoord.lat,
                  lng: centerCoord.lng,
                  radius: 300, // Slightly larger radius for map display
                  limit: 15
                }
              }
            );
            setCulturalPOIs(response.data || []);
          }
        }
      } else if (walk.routeCoordinates && walk.routeCoordinates.length > 0) {
        // Search POIs near route center
        const centerCoord = walk.routeCoordinates[Math.floor(walk.routeCoordinates.length / 2)];
        response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/cultural-pois/search`,
          {
            params: {
              lat: centerCoord.lat,
              lng: centerCoord.lng,
              radius: 300,
              limit: 15
            }
          }
        );
        setCulturalPOIs(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching cultural POIs for map:', error);
      setCulturalPOIs([]);
    } finally {
      setPoisLoading(false);
    }
  };

  // Helper function to get category emoji for POI markers
  const getCategoryIcon = (category) => {
    switch (category) {
      case 'food': return '🍕';
      case 'art': return '🎨';
      case 'architecture': return '🏛️';
      case 'business': return '🛍️';
      case 'museum': return '🏛️';
      case 'viewpoint': return '🌆';
      case 'cultural_venue': return '🎭';
      case 'hidden_gem': return '💎';
      default: return '📍';
    }
  };

  // Find the cultural POI matching a waypoint (by place_id or name)
  const findMatchingPOI = (wp) => {
    const savedPOIs = walk.culturalPois || [];
    const allPOIs = [...savedPOIs, ...culturalPOIs];
    const wpName = (wp.name || '').toLowerCase().trim();
    if (!wpName && !wp.place_id) return null;
    return allPOIs.find(poi => {
      if (wp.place_id && poi.place_id && wp.place_id === poi.place_id) return true;
      const poiName = (poi.name || poi.title || '').toLowerCase().trim();
      return wpName && poiName && (wpName === poiName || wpName.includes(poiName) || poiName.includes(wpName));
    });
  };

  // Fetch a Google Places photo for a place_id and update selectedHighlight
  const fetchPlacePhoto = (placeId) => {
    if (!placeId || !window.google?.maps?.places) return;
    const el = document.createElement('div');
    const service = new window.google.maps.places.PlacesService(el);
    service.getDetails(
      { placeId, fields: ['photos'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.photos?.length) {
          try {
            const photoUrl = place.photos[0].getUrl({ maxWidth: 400, maxHeight: 200 });
            setSelectedHighlight(prev => prev ? { ...prev, photoUrl } : prev);
          } catch {}
        }
      }
    );
  };

  // Stable identity strings for the directions useEffect dependency.
  // Using the full `walk` object would re-trigger on every render.
  const originKey = walk.startingPoint || walk.startingAddress || '';
  const destKey = walk.endingPoint || walk.endingAddress || '';
  const waypointKey = orderedWaypoints.map(wp => wp.address || wp.name || '').join('|');

  // Calculate route using Google Directions API.
  useEffect(() => {
    if (!isLoaded || !window.google?.maps) return;
    if (!originKey && !destKey) return;

    const startPos = toLatLng(walk.startingCoords);
    const endPos = toLatLng(walk.endingCoords);

    const origin = startPos
      ? new window.google.maps.LatLng(startPos.lat, startPos.lng)
      : originKey;
    const destination = endPos
      ? new window.google.maps.LatLng(endPos.lat, endPos.lng)
      : destKey;

    if (!origin || !destination) return;

    // Build intermediate waypoints — only include those with real coords
    // or addresses that look geocodable (contain a number, suggesting a
    // street address rather than a free-text description).
    const looksLikeAddress = (str) => str && /\d/.test(str);
    const intermediateWaypoints = orderedWaypoints.map(wp => {
      const pos = toLatLng(wp.coords);
      if (pos) return { location: new window.google.maps.LatLng(pos.lat, pos.lng), stopover: true };
      if (looksLikeAddress(wp.address)) return { location: wp.address, stopover: true };
      return null;
    }).filter(Boolean);

    // Google Directions supports max 8 intermediate waypoints per request.
    // If more, split into chained legs.
    const MAX_WAYPOINTS = 8;

    const fetchDirections = (orig, dest, wps) => {
      return new Promise((resolve, reject) => {
        const svc = new window.google.maps.DirectionsService();
        svc.route(
          {
            origin: orig,
            destination: dest,
            waypoints: wps,
            travelMode: window.google.maps.TravelMode.WALKING,
            optimizeWaypoints: false,
          },
          (result, status) => {
            if (status === 'OK') resolve(result);
            else reject(new Error(status));
          }
        );
      });
    };

    const calculateWalkRoute = async () => {
      setIsCalculating(true);
      setError(null);

      try {
        if (intermediateWaypoints.length <= MAX_WAYPOINTS) {
          // Single request
          const result = await fetchDirections(origin, destination, intermediateWaypoints);
          setDirections(result);
          extractResolvedPositions(result);
        } else {
          // Split into chunks. Each chunk uses the previous chunk's
          // destination as its origin, and the final chunk ends at the
          // real destination.
          const allStops = [
            origin,
            ...intermediateWaypoints.map(wp => wp.location),
            destination,
          ];

          const allResults = [];
          for (let i = 0; i < allStops.length - 1; i += MAX_WAYPOINTS + 1) {
            const chunkOrigin = allStops[i];
            const chunkEnd = Math.min(i + MAX_WAYPOINTS + 1, allStops.length - 1);
            const chunkDest = allStops[chunkEnd];
            const chunkWps = allStops.slice(i + 1, chunkEnd).map(loc => ({
              location: loc,
              stopover: true,
            }));
            const result = await fetchDirections(chunkOrigin, chunkDest, chunkWps);
            allResults.push(result);
          }

          // Use the first result for the DirectionsRenderer (draws the
          // polyline). For multi-chunk, merge the overview paths.
          if (allResults.length === 1) {
            setDirections(allResults[0]);
            extractResolvedPositions(allResults[0]);
          } else {
            // Merge: combine all legs into the first result
            const merged = allResults[0];
            for (let r = 1; r < allResults.length; r++) {
              merged.routes[0].legs.push(...allResults[r].routes[0].legs);
              merged.routes[0].overview_path.push(...allResults[r].routes[0].overview_path);
            }
            setDirections(merged);
            extractResolvedPositions(merged);
          }
        }
      } catch (err) {
        console.error('Directions request failed:', err.message);
        setError('Could not calculate route. Showing markers instead.');
      } finally {
        setIsCalculating(false);
      }
    };

    const extractResolvedPositions = (result) => {
      try {
        const legs = result.routes[0].legs;
        const positions = orderedWaypoints.map((wp, i) => {
          const explicit = toLatLng(wp.coords);
          if (explicit) return explicit;
          if (legs[i]) {
            const loc = legs[i].end_location;
            return { lat: loc.lat(), lng: loc.lng() };
          }
          return null;
        });
        setResolvedPositions(positions);
      } catch (e) {
        console.warn('Could not extract waypoint positions from directions', e);
      }
    };

    calculateWalkRoute();
  }, [isLoaded, originKey, destKey, waypointKey]);

  // Fetch Cultural POIs when component loads
  useEffect(() => {
    if (isLoaded) {
      fetchCulturalPOIs();
    }
  }, [isLoaded, walk.id, walk.routeCoordinates]);

  if (loadError) {
    return (
      <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <MapPin className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-600">Error loading map</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-2"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-sm text-gray-600">Loading map...</p>
        </div>
      </div>
    );
  }

  // Compute marker positions: prefer explicit coords, fall back to directions result
  const dirLegs = directions?.routes?.[0]?.legs;
  const geoLoc = (loc) => loc ? { lat: loc.lat(), lng: loc.lng() } : null;
  const startMarkerPos = toLatLng(walk.startingCoords)
    || (dirLegs ? geoLoc(dirLegs[0]?.start_location) : null);
  const endMarkerPos = toLatLng(walk.endingCoords)
    || (dirLegs ? geoLoc(dirLegs[dirLegs.length - 1]?.end_location) : null);

  return (
    <>
      <div className={compact ? "h-full relative" : "space-y-4"}>
        {/* Map Container */}
        <div className="relative h-full">
          {/* Expand button for compact mode */}
          {compact && (
            <Button
              size="sm"
              variant="secondary"
              className="absolute top-2 right-2 z-10 bg-white/90 hover:bg-white shadow-md"
              onClick={() => setIsExpanded(true)}
            >
              <Expand className="h-4 w-4" />
            </Button>
          )}
          
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={mapCenter}
            zoom={compact ? 12 : 13}
            options={{
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: !compact,
              zoomControl: true, // Always show zoom controls
              disableDefaultUI: false, // Enable UI controls
            }}
            onClick={compact ? () => setIsExpanded(true) : undefined}
          >
          {/* Render route if available */}
          {directions && (
            <DirectionsRenderer
              directions={directions}
              options={{
                suppressMarkers: true, // We'll show custom markers
                polylineOptions: {
                  strokeColor: '#7C3AED',
                  strokeWeight: 4,
                  strokeOpacity: 0.8
                }
              }}
            />
          )}

          {/* Starting point marker */}
          {startMarkerPos && (
            <Marker
              position={startMarkerPos}
              title={`Start: ${walk.startingPoint}`}
              label={{ text: 'S', color: 'white', fontSize: '11px', fontWeight: 'bold' }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: compact ? 9 : 13,
                fillColor: '#6D28D9',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2,
              }}
              zIndex={900}
              onClick={() => {
                if (onPOISelect) {
                  onPOISelect({
                    name: walk.startingPoint || 'Start',
                    address: walk.startingAddress || walk.startingPoint || '',
                    description: 'Starting point of the walk',
                    coordinates: startMarkerPos,
                    type: 'waypoint',
                  });
                } else {
                  setSelectedHighlight({
                    title: walk.startingPoint || 'Start',
                    description: 'Starting point of the walk',
                    coords: startMarkerPos,
                    type: 'start',
                  });
                }
              }}
            />
          )}

          {/* Numbered waypoint stops (blue) */}
          {orderedWaypoints.map((wp, index) => {
            const pos = toLatLng(wp.coords) || resolvedPositions[index];
            if (!pos) return null;
            return (
              <Marker
                key={`waypoint-${index}`}
                position={pos}
                title={wp.name || `Stop ${index + 1}`}
                zIndex={1100}
                label={{
                  text: (index + 1).toString(),
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
                icon={{
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: compact ? 9 : 11,
                  fillColor: '#2563eb',
                  fillOpacity: 1,
                  strokeColor: 'white',
                  strokeWeight: 2,
                }}
                onClick={() => {
                  if (onPOISelect) {
                    const matched = findMatchingPOI(wp);
                    if (matched) {
                      // Open the full POI modal with all cultural POI data
                      onPOISelect({
                        ...matched,
                        name: matched.name || matched.title || wp.name,
                        address: wp.address || matched.formatted_address || '',
                        coordinates: pos,
                        place_id: matched.place_id || wp.place_id || null,
                        type: 'highlight',
                      });
                    } else {
                      onPOISelect({
                        id: `waypoint-${index}`,
                        name: wp.name || `Stop ${index + 1}`,
                        address: wp.address || '',
                        description: wp.description || wp.address || '',
                        category: 'waypoint',
                        coordinates: pos,
                        type: 'waypoint',
                      });
                    }
                  }
                }}
              />
            );
          })}

          {/* Ending point marker */}
          {endMarkerPos && (!startMarkerPos || endMarkerPos.lat !== startMarkerPos.lat || endMarkerPos.lng !== startMarkerPos.lng) && (
            <Marker
              position={endMarkerPos}
              title={`End: ${walk.endingPoint}`}
              label={{ text: 'E', color: 'white', fontSize: '11px', fontWeight: 'bold' }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: compact ? 9 : 13,
                fillColor: '#6D28D9',
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2,
              }}
              zIndex={900}
              onClick={() => {
                if (onPOISelect) {
                  onPOISelect({
                    name: walk.endingPoint || 'End',
                    address: walk.endingAddress || walk.endingPoint || '',
                    description: 'Ending point of the walk',
                    coordinates: endMarkerPos,
                    type: 'waypoint',
                  });
                } else {
                  setSelectedHighlight({
                    title: walk.endingPoint || 'End',
                    description: 'Ending point of the walk',
                    coords: endMarkerPos,
                    type: 'end',
                  });
                }
              }}
            />
          )}

          {/* Cultural POI markers */}
          {culturalPOIs.filter(poi => poi.coordinates?.lat && poi.coordinates?.lng).map((poi, index) => (
            <Marker
              key={`poi-${poi.id}`}
              position={{ lat: poi.coordinates.lat, lng: poi.coordinates.lng }}
              title={`Cultural POI: ${poi.name} (${poi.category})`}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: compact ? 10 : 14,
                fillColor: '#f59e0b', // Orange for Cultural POIs
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2
              }}
              label={{
                text: (index + 1).toString(), // Number the POIs sequentially
                color: 'white',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
              onClick={() => {
                if (onPOISelect) {
                  onPOISelect({
                    ...poi,
                    name: poi.name || poi.title,
                    place_id: poi.place_id || poi.id,
                    coordinates: poi.coordinates || poi.coords,
                    type: 'highlight',
                  });
                }
              }}
            />
          ))}

          {/* Info window for selected highlight (only in full mode) */}
          {!compact && selectedHighlight && (
            <InfoWindow
              position={selectedHighlight.coords}
              onCloseClick={() => setSelectedHighlight(null)}
            >
              <div className="max-w-[280px]">
                {/* Place photo */}
                {selectedHighlight.photoUrl && (
                  <img
                    src={selectedHighlight.photoUrl}
                    alt={selectedHighlight.title}
                    className="w-full h-[120px] object-cover rounded-t-lg -mt-2 -mx-0"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <div className="p-3">
                <div className="flex items-start gap-2 mb-2">
                  {selectedHighlight.type === 'cultural_poi' && (
                    <span className="text-lg">{getCategoryIcon(selectedHighlight.category)}</span>
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm mb-1">
                      {selectedHighlight.title}
                    </h3>
                    {selectedHighlight.subcategory && (
                      <span className="inline-block px-2 py-1 bg-gray-100 text-xs rounded-md mb-2">
                        {selectedHighlight.subcategory.replace('-', ' ')}
                      </span>
                    )}
                  </div>
                </div>

                {selectedHighlight.address && (
                  <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3 flex-shrink-0" />
                    {selectedHighlight.address}
                  </p>
                )}
                <p className="text-xs text-gray-600 mb-2 leading-relaxed">
                  {selectedHighlight.description}
                </p>

                {/* Cultural POI specific information */}
                {selectedHighlight.type === 'cultural_poi' && (
                  <div className="space-y-2">
                    {selectedHighlight.cultural_significance && (
                      <div className="bg-blue-50 border-l-2 border-blue-400 pl-2 py-1">
                        <p className="text-blue-900 text-xs font-medium">Cultural Significance</p>
                        <p className="text-blue-800 text-xs leading-relaxed">{selectedHighlight.cultural_significance}</p>
                      </div>
                    )}
                    
                    {selectedHighlight.insider_tips && (
                      <div className="bg-green-50 border-l-2 border-green-400 pl-2 py-1">
                        <p className="text-green-900 text-xs font-medium">Insider Tip</p>
                        <p className="text-green-800 text-xs leading-relaxed">{selectedHighlight.insider_tips}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-3 text-xs text-gray-500 pt-1">
                      {selectedHighlight.estimated_visit_duration && (
                        <span>⏱️ {selectedHighlight.estimated_visit_duration} min</span>
                      )}
                      {selectedHighlight.user_ratings > 0 && (
                        <span>⭐ {selectedHighlight.user_ratings.toFixed(1)}</span>
                      )}
                    </div>

                    {selectedHighlight.website && (
                      <a 
                        href={selectedHighlight.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block mt-2 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        Visit Website
                      </a>
                    )}
                  </div>
                )}

                {/* Regular highlight type indicator */}
                {selectedHighlight.type === 'highlight' && (
                  <div className="flex items-center gap-1 mt-2">
                    <Star className="w-3 h-3 text-purple-500" />
                    <span className="text-xs text-purple-600">Walk Highlight</span>
                  </div>
                )}

                {/* Waypoint indicator */}
                {selectedHighlight.type === 'waypoint' && (
                  <div className="flex items-center gap-1 mt-2">
                    <MapPin className="w-3 h-3 text-blue-500" />
                    <span className="text-xs text-blue-600">Route Stop</span>
                  </div>
                )}

                {/* Start/End point indicators */}
                {(selectedHighlight.type === 'start' || selectedHighlight.type === 'end') && (
                  <div className="flex items-center gap-1 mt-2">
                    <MapPin className={`w-3 h-3 ${selectedHighlight.type === 'start' ? 'text-green-500' : 'text-red-500'}`} />
                    <span className={`text-xs ${selectedHighlight.type === 'start' ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedHighlight.type === 'start' ? 'Starting Point' : 'Ending Point'}
                    </span>
                  </div>
                )}
              </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>

        {/* Loading overlay */}
        {isCalculating && (
          <div className="absolute inset-0 bg-white/80 flex items-center justify-center rounded-lg">
            <div className="text-center">
              <Loader className="h-6 w-6 animate-spin text-blue-600 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Calculating route...</p>
            </div>
          </div>
        )}

        {/* POI Loading overlay */}
        {poisLoading && !compact && (
          <div className="absolute top-2 right-2 bg-white/90 px-3 py-2 rounded-lg shadow-sm">
            <div className="flex items-center gap-2">
              <Loader className="h-4 w-4 animate-spin text-blue-600" />
              <span className="text-xs text-gray-600">Loading POIs...</span>
            </div>
          </div>
        )}

        {/* Map Legend (only in full mode) */}
        {!compact && (
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-lg border">
            <h4 className="font-semibold text-xs mb-2 text-gray-800">Map Legend</h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-green-500 rounded-full border border-white"></div>
                <span>Start</span>
              </div>
              {orderedWaypoints.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-600 rounded-full border border-white"></div>
                  <span>Stops ({orderedWaypoints.length})</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full border border-white"></div>
                <span>End</span>
              </div>
              {culturalPOIs.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full border border-white text-white text-[8px] flex items-center justify-center">📍</div>
                  <span>Cultural POIs ({culturalPOIs.length})</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Full mode features only */}
      {!compact && (
        <>
          {/* Map Legend */}
          <div className="flex flex-wrap gap-4 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span>Start</span>
            </div>
            {orderedWaypoints.length > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                <span>{orderedWaypoints.length} Stops</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span>End</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-6 h-1 bg-blue-500 rounded"></div>
              <span>Walking Route</span>
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          )}

          {/* Get Directions Buttons */}
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                const sLat = Number(walk.startingCoords?.lat);
                const sLng = Number(walk.startingCoords?.lng);
                const eLat = Number(walk.endingCoords?.lat);
                const eLng = Number(walk.endingCoords?.lng);

                const stops = orderedWaypoints
                  .map((wp) => {
                    const lat = Number(wp.coords?.lat);
                    const lng = Number(wp.coords?.lng);
                    if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
                      return `${lat},${lng}`;
                    }
                    return null;
                  })
                  .filter(Boolean);

                const url = `https://www.google.com/maps/dir/?api=1`
                  + `&origin=${sLat},${sLng}`
                  + `&destination=${eLat},${eLng}`
                  + (stops.length ? `&waypoints=${stops.join('|')}` : '')
                  + `&travelmode=walking`;

                console.log('[WalkRouteMap] Walking directions URL:', url);
                console.log('[WalkRouteMap] startingCoords:', walk.startingCoords, '| endingCoords:', walk.endingCoords);
                console.log('[WalkRouteMap] orderedWaypoints:', orderedWaypoints);
                window.open(url, '_blank');
              }}
            >
              <Navigation className="mr-2 h-4 w-4" />
              Get Walking Directions
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              className="w-full text-sm"
              onClick={() => {
                const start = `${walk.startingCoords.lat},${walk.startingCoords.lng}`;
                window.open(`https://www.google.com/maps/dir/?api=1&destination=${start}&travelmode=driving`, '_blank');
              }}
            >
              <Car className="mr-2 h-3 w-3" />
              Driving Directions to Start
            </Button>
          </div>
        </>
      )}
    </div>

    {/* Expanded Map Modal */}
    {isExpanded && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-6xl h-full max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold">{walk.title} - Interactive Map</h3>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Expanded Map */}
          <div className="flex-1 relative">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={14}
              options={{
                streetViewControl: true,
                mapTypeControl: true,
                fullscreenControl: true,
                zoomControl: true,
                disableDefaultUI: false,
              }}
            >
              {/* All the same markers as the main map */}
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: '#7C3AED',
                      strokeWeight: 4,
                      strokeOpacity: 0.8
                    }
                  }}
                />
              )}

              {/* Starting point marker (green) */}
              {startMarkerPos && (
                <Marker
                  position={startMarkerPos}
                  title={`Start: ${walk.startingPoint}`}
                  label={{ text: 'S', color: 'white', fontSize: '11px', fontWeight: 'bold' }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 13,
                    fillColor: '#6D28D9',
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2,
                  }}
                  zIndex={1000}
                />
              )}

              {/* Numbered waypoint stops (blue) */}
              {orderedWaypoints.map((wp, index) => {
                const pos = toLatLng(wp.coords) || resolvedPositions[index];
                if (!pos) return null;
                return (
                  <Marker
                    key={`wp-modal-${index}`}
                    position={pos}
                    title={wp.name || `Stop ${index + 1}`}
                    zIndex={1100}
                    label={{
                      text: (index + 1).toString(),
                      color: 'white',
                      fontSize: '12px',
                      fontWeight: 'bold',
                    }}
                    icon={{
                      path: window.google.maps.SymbolPath.CIRCLE,
                      scale: 11,
                      fillColor: '#2563eb',
                      fillOpacity: 1,
                      strokeColor: 'white',
                      strokeWeight: 2,
                    }}
                    onClick={() => {
                      if (onPOISelect) {
                        onPOISelect({
                          id: `waypoint-${index}`,
                          name: wp.name || `Stop ${index + 1}`,
                          description: wp.description || wp.address || '',
                          category: 'waypoint',
                          coordinates: pos,
                          type: 'waypoint',
                        });
                        setIsExpanded(false);
                      }
                    }}
                  />
                );
              })}

              {/* Ending point marker (red) */}
              {endMarkerPos && (!startMarkerPos || endMarkerPos.lat !== startMarkerPos.lat || endMarkerPos.lng !== startMarkerPos.lng) && (
                <Marker
                  position={endMarkerPos}
                  title={`End: ${walk.endingPoint}`}
                  label={{ text: 'E', color: 'white', fontSize: '11px', fontWeight: 'bold' }}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 13,
                    fillColor: '#6D28D9',
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2,
                  }}
                  zIndex={1000}
                />
              )}

              {/* Cultural POI markers */}
              {culturalPOIs.filter(poi => poi.coordinates?.lat && poi.coordinates?.lng).map((poi, index) => (
                <Marker
                  key={`poi-${poi.id}`}
                  position={{ lat: poi.coordinates.lat, lng: poi.coordinates.lng }}
                  title={`Cultural POI: ${poi.name} (${poi.category})`}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    scale: 14,
                    fillColor: '#f59e0b',
                    fillOpacity: 1,
                    strokeColor: 'white',
                    strokeWeight: 2
                  }}
                  label={{
                    text: (index + 1).toString(),
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}
                  onClick={() => {
                    if (onPOISelect) {
                      onPOISelect({
                        ...poi,
                        name: poi.name || poi.title,
                        place_id: poi.place_id || poi.id,
                        coordinates: poi.coordinates || poi.coords,
                        type: 'highlight',
                      });
                      setIsExpanded(false);
                    }
                  }}
                />
              ))}
            </GoogleMap>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default WalkRouteMap;